import { Events, EventBus } from '@/lib/events'
import {
  EMPTY_ADDRESS,
  DEFAULT_META_DESCRIPTION,
} from '@/lib/constants/one-page-site'
import { getPaletteHexValue } from '../lib/helpers/logos'

export const state = () => ({
  site: null,
  sites: [],
  pages: [],
  page: null,
  new_site: true,

  // html site preview
  preview: '',

  // Available themes
  themes: [],

  // Available button styles
  button_types: [],
  button_shapes: [],

  // Available fonts
  available_fonts: [],

  // Available social icon type
  social_icon_types: [],

  // Subdomain available
  subdomain_available: true,

  // Domain Available
  domain_available: false,

  // Custom domain setup
  custom_domain_loading: false,
  custom_domain_info: {},
  selected_domain: {},

  // Site Config
  site_config: {},
  site_id: '',
  subdomain: '',
  custom_domain: '',
  meta: {
    title: '',
    description: '',
    keywords: '',
  },
  title: '',
  subtitle: '',
  theme: 'theme_3',
  colors: {
    primary: '#ffffff',
    background: '#000000',
  },
  custom_colors: {},
  button_styles: {},
  fonts: { primary: 'Inter' },
  background_images: [],
  social_icon_styles: {},
  sections: [],
  header_section: {
    type: 'header',
    enabled: true,
    components: [
      {
        type: 'header_logo',
      },
      {
        type: 'header_text',
        text: {
          title: '',
          subtitle: '',
        },
      },
    ],
  },
  profile_image_upload: '',
  links_section: null,
  links: [],
  links_enabled: true,
  social_icons_section: null,
  social_icons: [],
  social_icons_enabled: true,
  address_section: null,
  address: EMPTY_ADDRESS,
  address_enabled: true,
  google_maps_section: null,
  google_maps_enabled: false,
  google_maps_query: '',
  footer_section: null,
  footer_enabled: true,
  logo_id: '',
  user_id: '',
  upgraded: false,
  published: false,

  available_icons: [{ handle: 'none', label: 'No Icon' }],

  // Debounce db updates
  timeoutSections: null,
  updatingSections: false,
  timeoutSubdomain: null,
  checkingSubdomain: false,
  timeoutDomain: null,
  checkingDomain: false,
  timeoutMeta: null,
  updatingMeta: false,
  timeoutThemeColors: null,
  updatingThemeColors: false,
  timeoutCustomColors: null,
  updatingCustomColors: false,
  debounceTime: 250,
})

export const mutations = {
  async updateSiteConfig(state) {
    state.site_config = {
      site_id: state.site.id,
      subdomain: state.subdomain,
      custom_domain: state.custom_domain,
      meta: state.meta,
      theme: state.theme,
      colors: state.colors,
      sections: state.sections,
      logo_id: state.logo_id,
      user_id: state.user_id,
      upgraded: state.upgraded,
    }
  },

  setProperty(state, { key, value }) {
    state[key] = value
  },

  setSite(state, site) {
    state.site = site
    state.site_id = site.id
    state.subdomain = site.subdomain
    state.custom_domain = site.custom_domain
    state.upgraded = site.upgraded ?? false
    state.published = site.published ?? false
  },

  setMeta(state, change = {}) {
    state.meta = { ...state.meta, ...change }
  },

  updateObjectProperty(state, { key, change }) {
    if (!state[key] || !(typeof state[key] === 'object')) return

    const updated = { ...state[key], ...change }

    // Remove nullish values
    const reduced = Object.keys(updated)
      .filter(filterKey => updated[filterKey])
      .reduce(
        (valid, validKey) => ({
          ...valid,
          [validKey]: updated[validKey],
        }),
        {}
      )

    state[key] = { ...reduced }
  },
}

export const getters = {
  getTitle(state) {
    return state.title
  },

  getSubtitle(state) {
    return state.subtitle
  },

  getLinks(state) {
    return state.links
  },

  getAddress(state) {
    return state.address
  },

  getAddressEnabled(state) {
    return state.address_enabled
  },

  getFooterEnabled(state) {
    return state.footer_enabled
  },

  /**
   * Get the subdomain component from the custom_domain
   */
  getCustomDomainSubdomain(state) {
    if (!state.custom_domain) {
      return ''
    }
    const regex = /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/
    // Remove the last part of the domain [domain].[tld]/[path(s)]
    let subdomain = state.custom_domain.replace(regex, '')
    // Remove the last period from the subdomain
    if (subdomain && subdomain.slice(-1) === '.') {
      subdomain = subdomain.slice(0, -1)
    }
    return subdomain
  },
}

export const actions = {
  /**
   * Initialize the site_config using the given logo object
   */
  async initializeEditor(
    { state, commit, dispatch },
    { logo, siteId, upgraded, skipPreviewRefresh } = {}
  ) {
    // If there is a provided siteId try to use that
    // Else look up sites connected to logo
    if (siteId) {
      await dispatch('retrieveSite', siteId)
      commit('setProperty', { key: 'new_site', value: false })
    } else if (!logo) {
      //  No Logo Case
      return
    } else {
      // Get sites connected to the logo
      const sites = await dispatch('listSites', {
        logo_id: logo.id,
      })

      // If the user has no sites, create a new one
      // Else use the first site for the logo
      if (!sites || sites.length === 0) {
        const { palette } = logo.assets
        const primary = getPaletteHexValue(palette.primary)
        const background = getPaletteHexValue(palette.background)

        const siteBody = {
          logo_id: logo.id,
          metadata: {
            title: `${logo.wordmark} | LOGO.com`,
            description: logo.slogan || DEFAULT_META_DESCRIPTION,
            keywords: logo.wordmark,
          },
          upgraded: upgraded || false,
          theme: 'theme_3',
          color_primary: primary,
          color_background: background,
        }
        await dispatch('createSite', siteBody)
        commit('setProperty', { key: 'new_site', value: true })

        // Get a default subdomain based on wordmark (lowercase/no spaces)
        const defaultSubdomain = logo.wordmark
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
        if (defaultSubdomain) {
          commit('setProperty', { key: 'subdomain', value: defaultSubdomain })
        }
      } else {
        commit('setSite', sites[0])
        commit('setProperty', { key: 'new_site', value: false })
      }
    }

    const { site } = state
    const pages = await dispatch('listPages', site.id)
    const firstPage = pages[0]

    await dispatch('checkSubdomain')
    await dispatch('getButtonTypes')
    await dispatch('getButtonShapes')
    await dispatch('getFonts')
    await dispatch('getSocialIconTypes')
    await dispatch('setupPage', { page: firstPage, skipPreviewRefresh })
    await dispatch('getAvailableIcons')
    await dispatch('handleLogoUpdates', {
      logo,
      skipPreviewRefresh,
    })
  },

  /**
   * Set properties based on a given page in db
   */
  async setupPage({ state, commit, dispatch }, { page, skipPreviewRefresh }) {
    const { site } = state

    await commit('setProperty', { key: 'page', value: page })
    await commit('setProperty', { key: 'meta', value: page.metadata })
    await commit('setProperty', { key: 'theme', value: page.theme })
    await commit('setProperty', {
      key: 'colors',
      value: {
        primary: page.color_primary,
        background: page.color_background,
      },
    })
    await commit('setProperty', {
      key: 'custom_colors',
      value: page.custom_colors || {},
    })
    await commit('setProperty', {
      key: 'button_styles',
      value: page.button_styles || {},
    })
    await commit('setProperty', {
      key: 'fonts',
      value: page.fonts || { primary: 'Inter' },
    })
    await commit('setProperty', {
      key: 'background_images',
      value: page.background_images || [],
    })
    await commit('setProperty', {
      key: 'social_icon_styles',
      value: page.social_icon_styles || {},
    })

    // Header Section
    const headerSection = page.sections.find(
      section => section.type === 'header'
    )
    await commit('setProperty', {
      key: 'header_section',
      value: headerSection,
    })
    const headerLogoComponent =
      headerSection?.components.find(
        component => component.type === 'header_logo'
      ) || null
    await commit('setProperty', {
      key: 'profile_image_upload',
      value: headerLogoComponent?.upload || '',
    })
    const headerTextComponent =
      headerSection?.components.find(
        component => component.type === 'header_text'
      ) || null
    await commit('setProperty', {
      key: 'title',
      value: headerTextComponent?.text.title || '',
    })
    await commit('setProperty', {
      key: 'subtitle',
      value: headerTextComponent?.text.subtitle || '',
    })

    // Links
    const linksSection = page.sections.find(section =>
      section.components.find(component => component.type === 'link_list')
    )
    const linkListComponent =
      linksSection?.components.find(
        component => component.type === 'link_list'
      ) || null
    await commit('setProperty', {
      key: 'links',
      value: linkListComponent?.items || [],
    })
    await commit('setProperty', {
      key: 'links_section',
      value: linksSection || null,
    })

    // Social Icons
    const socialIconListSection = page.sections.find(section =>
      section.components.find(
        component => component.type === 'social_icon_list'
      )
    )
    const socialIconListComponent =
      socialIconListSection?.components.find(
        component => component.type === 'social_icon_list'
      ) || null
    await commit('setProperty', {
      key: 'social_icons',
      value: socialIconListComponent?.items || [],
    })
    await commit('setProperty', {
      key: 'social_icons_section',
      value: socialIconListSection || null,
    })
    await commit('setProperty', {
      key: 'social_icons_enabled',
      value: socialIconListComponent?.enabled ?? true,
    })

    // Address
    const addressSection = page.sections.find(section =>
      section.components.find(component => component.type === 'address')
    )
    const addressComponent =
      addressSection?.components.find(
        component => component.type === 'address'
      ) || null
    await commit('setProperty', {
      key: 'address',
      value: addressComponent?.address || EMPTY_ADDRESS,
    })
    await commit('setProperty', {
      key: 'address_section',
      value: addressSection || null,
    })
    await commit('setProperty', {
      key: 'address_enabled',
      value: addressComponent?.enabled ?? true,
    })

    // Google Maps
    let googleMapsComponent
    const googleMapsSection = page.sections.find(section => {
      googleMapsComponent = section.components.find(
        component => component.type === 'google_maps'
      )
      return googleMapsComponent
    })
    await commit('setProperty', {
      key: 'google_maps_section',
      value: googleMapsSection || null,
    })
    await commit('setProperty', {
      key: 'google_maps_query',
      value: googleMapsComponent?.map_query ?? '',
    })
    await commit('setProperty', {
      key: 'google_maps_enabled',
      value: googleMapsComponent?.enabled ?? false,
    })

    // Footer
    let footerComponent
    const footerSection = page.sections.find(section => {
      footerComponent = section.components.find(
        component => component.type === 'logo_footer'
      )
      return footerComponent
    })
    await commit('setProperty', {
      key: 'footer_section',
      value: footerSection || null,
    })
    await commit('setProperty', {
      key: 'footer_enabled',
      value: footerComponent?.enabled ?? true,
    })

    // Rebuild Sections
    await dispatch('updateSections', { skipPreviewRefresh })

    // User/Brand Info
    await commit('setProperty', { key: 'logo_id', value: site.logo_id })
    await commit('setProperty', { key: 'user_id', value: site.user_id })

    // Rebuild Site Config
    await commit('updateSiteConfig')

    // Get Themes
    await dispatch('getSiteThemes')
  },

  /**
   * Update the db if logo has been updated
   */
  async handleLogoUpdates(
    { state, commit, dispatch },
    {
      logo,
      oldLogo,
      forceUpdate,
      skipTitle,
      skipSubtitle,
      skipMeta,
      skipPreviewRefresh,
    }
  ) {
    if (!logo || !logo.id) return

    // If the logo has been updated since the page was updated,
    // update the db to reflect the changes
    if (state.page.date_updated < logo.updated_at || forceUpdate) {
      // Update logo colors
      const colors = { ...state.colors }
      let colorUpdates = {}

      const { palette } = logo.assets
      const primary = getPaletteHexValue(palette.primary)
      const background = getPaletteHexValue(palette.background)

      if (state.colors.primary !== primary) {
        colorUpdates = {
          ...colorUpdates,
          color_primary: primary,
        }
        colors.primary = primary
      }
      if (state.colors.background !== background) {
        colorUpdates = {
          ...colorUpdates,
          color_background: background,
        }
        colors.background = background
      }

      if (Object.keys(colorUpdates).length > 0) {
        await commit('setProperty', { key: 'colors', value: colors })
        await dispatch('updatePage', colorUpdates)

        // Refresh Themes
        dispatch('getSiteThemes')
      }

      /**
       * Update header in case wordmark/slogan changed
       */
      const oldPageSubtitle = state.subtitle
      if (!skipTitle || !skipSubtitle) {
        if (!skipTitle) {
          await commit('setProperty', {
            key: 'title',
            value: logo.wordmark,
          })
        }
        if (!skipSubtitle) {
          await commit('setProperty', {
            key: 'subtitle',
            value: logo.slogan,
          })
        }
        await dispatch('formatHeaderSection', { skipPreviewRefresh })
      }

      /**
       * Update meta if logo changed
       * If the site is not upgraded, completely replace with defaults.
       * Else if oldLogo is provided, use that to partially string
       * replace the meta.
       */
      if (!skipMeta) {
        const newMeta = {}

        const oldMetaTitle = state.meta.title
        const oldMetaDescription = state.meta.description

        if (!state.upgraded) {
          newMeta.title = `${logo.wordmark} | LOGO.com`

          newMeta.description = state.subtitle || DEFAULT_META_DESCRIPTION
        } else if (oldLogo) {
          /**
           * Replace old wordmark in meta title
           */
          newMeta.title = oldMetaTitle.replace(
            new RegExp(oldLogo.wordmark, 'gi'),
            logo.wordmark
          )

          /**
           * If current description is either of old default options, update
           * them using new default options.
           * Else if it is custom, try to partially replace the wordmark.
           */
          if (
            !oldMetaDescription ||
            oldMetaDescription === oldPageSubtitle ||
            oldMetaDescription === DEFAULT_META_DESCRIPTION
          ) {
            newMeta.description = state.subtitle || DEFAULT_META_DESCRIPTION
          } else {
            newMeta.description = oldMetaDescription.replace(
              new RegExp(oldLogo.wordmark, 'gi'),
              logo.wordmark
            )
          }
        }

        await commit('setMeta', newMeta)
        await dispatch('updateMeta')
      }
    }
  },

  /**
   * Get a user's site by id
   */
  async retrieveSite({ commit }, siteId) {
    const { data: site } = await this.$api.get(
      `/api/v3/one-page-sites/${siteId}`
    )

    if (site && site.id) {
      await commit('setSite', site)
    }

    return site
  },

  /**
   * List a user's sites
   */
  async listSites({ commit }, params = {}) {
    const { data } = await this.$api.get(`/api/v3/one-page-sites/`, { params })
    const sites = data.data

    await commit('setProperty', { key: 'sites', value: sites })

    return sites
  },

  /**
   * Create new site
   */
  async createSite({ commit }, body) {
    const { data: site } = await this.$api.post(`/api/v3/one-page-sites`, {
      logo_id: '',
      metadata: {
        title: '',
        description: '',
        keywords: '',
      },
      color_primary: '#ffffff',
      color_background: '#000000',
      ...body,
    })

    await commit('setSite', site)
  },

  /**
   * List a sites pages
   */
  async listPages({ commit }, siteId) {
    const { data } = await this.$api.get(
      `/api/v3/one-page-sites/${siteId}/pages`
    )
    const pages = data.data

    await commit('setProperty', { key: 'pages', value: pages })

    return pages
  },

  /**
   * Format the headers section object
   */
  async formatHeaderSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    await commit('setProperty', {
      key: 'header_section',
      value: {
        type: 'header',
        enabled: true,
        components: [
          {
            type: 'header_logo',
            upload: state.profile_image_upload,
          },
          {
            type: 'header_text',
            text: {
              title: state.title,
              subtitle: state.subtitle,
            },
          },
        ],
      },
    })

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Format the Links section object
   */
  async formatLinkSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    if (state.links.length === 0) {
      await commit('setProperty', { key: 'links_section', value: null })
    } else {
      await commit('setProperty', {
        key: 'links_section',
        value: {
          type: 'section',
          enabled: true,
          components: [
            {
              type: 'link_list',
              enabled: state.links_enabled,
              items: state.links.map(link => ({
                text: link.text,
                link: link.link,
                enabled: link.enabled,
                icon: link.icon || '',
              })),
            },
          ],
        },
      })
    }

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Format the Social Icons section object
   */
  async formatSocialIconsSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    if (state.social_icons.length === 0) {
      await commit('setProperty', { key: 'social_icons_section', value: null })
    } else {
      await commit('setProperty', {
        key: 'social_icons_section',
        value: {
          type: 'section',
          enabled: true,
          components: [
            {
              type: 'social_icon_list',
              enabled: state.social_icons_enabled,
              items: state.social_icons.map(link => ({
                text: link.text || '',
                link: link.link,
                enabled: link.enabled,
                icon: link.icon || '',
              })),
            },
          ],
        },
      })
    }

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Format the Address section object
   */
  async formatAddressSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    if (!state.address) {
      await commit('setProperty', { key: 'address_section', value: null })
    } else {
      await commit('setProperty', {
        key: 'address_section',
        value: {
          type: 'section',
          enabled: true,
          components: [
            {
              type: 'address',
              enabled: state.address_enabled,
              address: state.address,
            },
          ],
        },
      })
    }

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Format the Google Maps section object
   */
  async formatGoogleMapsSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    await commit('setProperty', {
      key: 'google_maps_section',
      value: {
        type: 'section',
        enabled: true,
        components: [
          {
            type: 'google_maps',
            map_query: state.google_maps_query,
            enabled: state.google_maps_enabled,
          },
        ],
      },
    })

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Format the Footer section object
   */
  async formatFooterSection(
    { state, dispatch, commit },
    { skipPreviewRefresh } = {}
  ) {
    await commit('setProperty', {
      key: 'footer_section',
      value: {
        type: 'footer',
        enabled: true,
        components: [
          {
            type: 'logo_footer',
            enabled: state.footer_enabled,
          },
        ],
      },
    })

    await dispatch('updateSections', { skipPreviewRefresh })
  },

  /**
   * Build the sections array and update sections in db
   */
  async updateSections(
    { state, commit, dispatch },
    { skipPreviewRefresh } = {}
  ) {
    const socialIconPosition = state.social_icon_styles?.position || 'top'
    let sections = [
      state.header_section,
      socialIconPosition === 'top' && state.social_icons_section,
      state.links_section,
      state.google_maps_section,
      state.address_section,
      socialIconPosition === 'bottom' && state.social_icons_section,
      state.footer_section,
    ]

    // Remove null sections
    sections = sections.filter(section => section)

    await commit('setProperty', { key: 'sections', value: sections })
    await commit('updateSiteConfig')

    // Debounce db update
    await commit('setProperty', { key: 'updatingSections', value: true })
    clearTimeout(state.timeoutSections)
    const timeout = setTimeout(async () => {
      await dispatch('updatePage', { sections })
      if (!skipPreviewRefresh) {
        await dispatch('previewSite')
      }
      await commit('setProperty', { key: 'updatingSections', value: false })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutSections', value: timeout })
  },

  /**
   * Update theme in db
   */
  async updateTheme({ state, commit, dispatch }, { skipPreviewRefresh } = {}) {
    await commit('updateSiteConfig')

    await dispatch('updatePage', { theme: state.theme })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update site theme colors in db
   */
  async updateThemeColors(
    { state, commit, dispatch },
    { newColors, skipPreviewRefresh } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('setProperty', {
      key: 'colors',
      value: { ...state.colors, ...newColors },
    })
    await commit('setProperty', { key: 'updatingThemeColors', value: true })
    clearTimeout(state.timeoutThemeColors)
    const timeout = setTimeout(async () => {
      await dispatch('updatePage', {
        color_primary: state.colors.primary,
        color_background: state.colors.background,
      })
      await dispatch('getSiteThemes')
      if (!skipPreviewRefresh) {
        await dispatch('previewSite')
      }
      await commit('setProperty', { key: 'updatingThemeColors', value: false })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutThemeColors', value: timeout })
  },

  /**
   * Update custom colors in db
   */
  async updateCustomColors(
    { state, commit, dispatch },
    { newColors, skipPreviewRefresh } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('updateObjectProperty', {
      key: 'custom_colors',
      change: newColors,
    })

    await commit('setProperty', { key: 'updatingCustomColors', value: true })
    clearTimeout(state.timeoutCustomColors)
    const timeout = setTimeout(async () => {
      await dispatch('updatePage', { custom_colors: state.custom_colors })
      if (!skipPreviewRefresh) {
        await dispatch('previewSite')
      }
      await commit('setProperty', {
        key: 'updatingCustomColors',
        value: false,
      })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutCustomColors', value: timeout })
  },

  /**
   * Reset custom colors in db
   */
  async resetCustomColors(
    { state, commit, dispatch },
    { skipPreviewRefresh } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('setProperty', { key: 'custom_colors', value: {} })
    await dispatch('updatePage', { custom_colors: state.custom_colors })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update button style in custom styles
   */
  async updateButtonStyle(
    { state, commit, dispatch },
    { skipPreviewRefresh, change } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('updateObjectProperty', {
      key: 'button_styles',
      change,
    })

    await dispatch('updatePage', { button_styles: state.button_styles })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update fonts
   */
  async updateFonts(
    { state, commit, dispatch },
    { skipPreviewRefresh, change } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('updateObjectProperty', {
      key: 'fonts',
      change,
    })

    await dispatch('updatePage', { fonts: state.fonts })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update background images
   */
  async updateBackgroundImages(
    { state, commit, dispatch },
    { skipPreviewRefresh, newImages } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('setProperty', { key: 'background_images', value: newImages })

    await dispatch('updatePage', { background_images: state.background_images })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update social icon style in custom styles
   */
  async updateSocialIconStyle(
    { state, commit, dispatch },
    { skipPreviewRefresh, change } = {}
  ) {
    await commit('updateSiteConfig')

    await commit('updateObjectProperty', {
      key: 'social_icon_styles',
      change,
    })

    await dispatch('updatePage', {
      social_icon_styles: state.social_icon_styles,
    })
    if (!skipPreviewRefresh) {
      await dispatch('previewSite')
    }
  },

  /**
   * Update meta in db
   */
  async updateMeta({ state, commit, dispatch }) {
    await commit('updateSiteConfig')

    // Debounce db update
    await commit('setProperty', { key: 'updatingMeta', value: true })
    clearTimeout(state.timeoutMeta)
    const timeout = setTimeout(async () => {
      await dispatch('updatePage', { metadata: state.meta })
      await commit('setProperty', { key: 'updatingMeta', value: false })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutMeta', value: timeout })
  },

  /**
   * Check if a subdomain is available in db
   */
  async checkSubdomainAvailability(context, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString()

      const { data } = await this.$api.get(
        `/api/v3/one-page-sites/subdomain/available?${queryString}`
      )
      const { available } = data
      return available
    } catch (error) {
      return false
    }
  },

  /**
   * Check if a subdomain is available in db
   */
  async checkSubdomain({ state, commit, dispatch }) {
    await commit('updateSiteConfig')

    // Debounce db update
    await commit('setProperty', { key: 'checkingSubdomain', value: true })
    clearTimeout(state.timeoutSubdomain)
    const timeout = setTimeout(async () => {
      // Exclude this site from the availability search - so that current
      // subdomain isn't flagged
      const queryParams = {
        subdomain: state.subdomain,
        exclude_site_id: state.site_id,
      }

      const available = await dispatch(
        'checkSubdomainAvailability',
        queryParams
      )

      await commit('setProperty', {
        key: 'subdomain_available',
        value: available,
      })

      await commit('setProperty', { key: 'checkingSubdomain', value: false })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutSubdomain', value: timeout })
  },

  /**
   * Update subdomain
   */
  async updateSubdomain({ state, commit, dispatch }) {
    await commit('updateSiteConfig')

    await dispatch('updateSite', { body: { subdomain: state.subdomain } })
  },

  /**
   * Update the current site
   */
  async updateSite({ state }, { site, body } = {}) {
    const siteId = site || state.site.id

    if (!siteId) return
    await this.$api.patch(`/api/v3/one-page-sites/${siteId}`, body)
  },

  /**
   * Update the current page
   */
  async updatePage({ state }, body) {
    if (!state.page) return
    await this.$api.patch(
      `/api/v3/one-page-sites/${state.site_id}/pages/${state.page.id}`,
      body
    )
  },

  /**
   * Build the static site from the current db entry and store it in
   * the R2 bucket
   */
  async buildSite({ state }, id) {
    let siteId = ''
    if (id) {
      siteId = id
    } else {
      siteId = state.site.id
    }
    const { data: siteConfig } = await this.$api.post(
      `/api/v3/one-page-sites/${siteId}/build`
    )

    /**
     * Find data in site config to use in mixpanel event
     */
    const firstPage = siteConfig.pages[0]
    const linksSection = firstPage?.sections?.find(section =>
      section.components?.find(component => component.type === 'link_list')
    )
    const linksComponent = linksSection?.components?.find(
      component => component.type === 'link_list'
    )
    const links = linksComponent?.items || []

    EventBus.$emit(Events.OnePageSitePublished, {
      links,
      theme: firstPage.theme,
      subdomain: siteConfig.subdomain,
      custom_domain: siteConfig.custom_domain,
      social_icon_type: firstPage.social_icon_styles?.type || 'filled',
    })

    return siteConfig
  },

  /**
   * Unpublish the site in the db and delete the static site from the R2 bucket
   */
  async unpublishSite({ state }, id) {
    let siteId = ''
    if (id) {
      siteId = id
    } else {
      siteId = state.site.id
    }
    const { data } = await this.$api.post(
      `/api/v3/one-page-sites/${siteId}/unpublish`
    )
    return data
  },

  /**
   * Delete the site in the db and delete the static site from the R2 bucket
   */
  async deleteSite({ state }, id) {
    let siteId = ''
    if (id) {
      siteId = id
    } else {
      siteId = state.site.id
    }
    const { data } = await this.$api.delete(`/api/v3/one-page-sites/${siteId}`)
    return data
  },

  /**
   * Check domain availablity
   */
  async checkDomain({ state, commit, getters }) {
    await commit('updateSiteConfig')

    if (!state.custom_domain) {
      return
    }

    // Debounce db update
    await commit('setProperty', { key: 'checkingDomain', value: true })
    clearTimeout(state.timeoutDomain)
    const timeout = setTimeout(async () => {
      try {
        // Add the www subdomain if there isnt one already
        const subdomain = getters.getCustomDomainSubdomain

        let domain = state.custom_domain
        if (!subdomain) {
          domain = `www.${domain}`
        }

        // Exclude this site from the availability search - so that current
        // domain isn't flagged
        const queryParams = {
          domain,
          exclude_site_id: state.site_id,
        }
        const queryString = new URLSearchParams(queryParams).toString()

        const { data } = await this.$api.get(
          `/api/v3/one-page-sites/domain/available?${queryString}`
        )
        const { available } = data
        await commit('setProperty', {
          key: 'domain_available',
          value: available,
        })
      } catch (error) {
        await commit('setProperty', {
          key: 'domain_available',
          value: false,
        })
      }
      await commit('setProperty', { key: 'checkingDomain', value: false })
    }, state.debounceTime)
    await commit('setProperty', { key: 'timeoutDomain', value: timeout })
  },

  /**
   * Build custom_domain in db
   */
  async buildCustomDomain({ state, commit, dispatch, getters }) {
    commit('setProperty', { key: 'custom_domain_loading', value: true })
    await commit('updateSiteConfig')

    if (!state.custom_domain) return

    // Add the www subdomain if there isnt one already
    const subdomain = getters.getCustomDomainSubdomain

    if (!subdomain) {
      await commit('setProperty', {
        key: 'custom_domain',
        value: `www.${state.custom_domain}`,
      })
    }

    await dispatch('checkDomain')

    if (state.domain_available) {
      const body = {
        domain: state.custom_domain,
        linked_domain_id: state.selected_domain.id || null,
      }

      try {
        await this.$api.post(
          `/api/v3/one-page-sites/${state.site_id}/custom-domain`,
          body
        )

        // Emit event on creation
        EventBus.$emit(Events.OnePageSiteCreateCustomDomain, {
          one_page_link: state.custom_domain,
          purchase_source: state.selected_domain.id ? 'LOGO.com' : 'External',
        })
      } catch (error) {
        // Ignore Error
      }
    }
    commit('setProperty', { key: 'custom_domain_loading', value: false })
  },

  /**
   * Get custom_domain in db for current site
   */
  async retrieveCustomDomainInfo({ state, commit }, site = null) {
    commit('setProperty', { key: 'custom_domain_loading', value: true })
    let results = null
    try {
      const { data } = await this.$api.get(
        `/api/v3/one-page-sites/${site || state.site_id}/custom-domain`
      )

      results = data
    } catch (error) {
      // Ignore Error
    }
    commit('setProperty', { key: 'custom_domain_loading', value: false })
    return results
  },

  /**
   * Update custom_domain_info
   */
  async updateCustomDomainInfo({ state, commit, dispatch }) {
    const results = await dispatch('retrieveCustomDomainInfo', state.site_id)
    commit('setProperty', { key: 'custom_domain_info', value: results })
  },

  /**
   * Delete custom_domain in db
   */
  async deleteCustomDomain({ state, commit }) {
    commit('setProperty', { key: 'custom_domain_loading', value: true })
    await commit('updateSiteConfig')

    if (!state.custom_domain) return

    try {
      await this.$api.delete(
        `/api/v3/one-page-sites/${state.site_id}/custom-domain`
      )
    } catch (error) {
      // Ignore Error
    }
    commit('setProperty', { key: 'custom_domain_loading', value: false })
  },

  /**
   * Update domain dns records to godaddy domain
   * Used for have logo flow
   */
  async getDomainRecords({ state }) {
    if (!state.selected_domain.id) {
      return
    }

    try {
      await this.$api.get(`/api/v3/domains/${state.selected_domain.id}/records`)
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Update domain dns records to godaddy domain
   * Used for have logo flow
   */
  async updateDomainRecords(
    { state, getters },
    { ownershipVerification, certificateValidation }
  ) {
    if (!state.selected_domain.id || !getters.getCustomDomainSubdomain) {
      return
    }

    // Records to add
    const records = [
      {
        type: 'CNAME',
        name: getters.getCustomDomainSubdomain,
        data: 'fallback.hi.link',
        ttl: 3600,
      },
    ]

    function replaceDomainWithSubdomain(value) {
      return value.replace(
        state.custom_domain,
        getters.getCustomDomainSubdomain
      )
    }

    // If an ownership verification record is provided, add it
    if (
      ownershipVerification &&
      ownershipVerification.name &&
      ownershipVerification.value
    ) {
      records.push({
        type: 'TXT',
        name: replaceDomainWithSubdomain(ownershipVerification.name),
        data: ownershipVerification.value,
        ttl: 3600,
      })
    }

    // If an certificate validation record is provided, add it
    if (
      certificateValidation &&
      certificateValidation.name &&
      certificateValidation.value
    ) {
      records.push({
        type: 'TXT',
        name: replaceDomainWithSubdomain(certificateValidation.name),
        data: certificateValidation.value,
        ttl: 3600,
      })
    }

    const body = { records }

    try {
      await this.$api.patch(
        `/api/v3/domains/${state.selected_domain.id}/records`,
        body
      )
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Add automatic forwarding to www subdomain
   * Used for have logo flow
   */
  async addDomainForwarding({ state, dispatch }) {
    if (!state.custom_domain) return

    const body = {
      type: 'REDIRECT_TEMPORARY',
      url: `https://${state.custom_domain}`,
    }

    await dispatch(
      'domains/addForwardingRule',
      { id: state.selected_domain.id, body },
      {
        root: true,
      }
    )
  },

  /**
   * Remove automatic forwarding
   */
  async removeDomainForwarding({ dispatch }, id) {
    await dispatch('domains/removeForwardingRules', id, {
      root: true,
    })
  },

  /**
   * Upgrade site
   */
  async upgradeSite({ state, commit }, id) {
    let siteId = ''
    if (id) {
      siteId = id
    } else {
      siteId = state.site.id
    }
    await this.$api.patch(`/api/v3/one-page-sites/${siteId}/upgrade`)
    commit('setProperty', { key: 'upgraded', value: true })
  },

  /**
   * Downgrade site
   */
  async downgradeSite({ state, commit }, id) {
    let siteId = ''
    if (id) {
      siteId = id
    } else {
      siteId = state.site.id
    }
    await this.$api.patch(`/api/v3/one-page-sites/${siteId}/downgrade`)
    commit('setProperty', { key: 'upgraded', value: false })
  },

  /**
   * Preview the site from the current db entry
   */
  async previewSite({ state, commit }) {
    const { data: preview } = await this.$api.post(
      `/api/v3/one-page-sites/${state.site.id}/pages/${state.page.id}/preview`
    )

    await commit('setProperty', { key: 'preview', value: preview })

    return preview
  },

  /**
   * Preview the site from the current db entry
   */
  async getSiteThemes({ state, commit }, { site, page } = {}) {
    const siteId = site || state.site.id
    const pageId = page || state.page.id

    const { data: themes } = await this.$api.post(
      `/api/v3/one-page-sites/${siteId}/pages/${pageId}/themes`
    )

    await commit('setProperty', { key: 'themes', value: themes })

    return themes
  },

  /**
   * Get available icons for links
   */
  async getAvailableIcons({ commit }) {
    const { data: icons } = await this.$api.get(`/api/v3/one-page-sites/icons`)

    await commit('setProperty', {
      key: 'available_icons',
      value: icons,
    })
  },

  /**
   * Get button types
   */
  async getButtonTypes({ commit }) {
    const { data: button_types } = await this.$api.get(
      `/api/v3/one-page-sites/button-types`
    )

    await commit('setProperty', {
      key: 'button_types',
      value: button_types,
    })
  },

  /**
   * Get button shapes
   */
  async getButtonShapes({ commit }) {
    const { data: button_shapes } = await this.$api.get(
      `/api/v3/one-page-sites/button-shapes`
    )

    await commit('setProperty', {
      key: 'button_shapes',
      value: button_shapes,
    })
  },

  /**
   * Get fonts
   */
  async getFonts({ commit }) {
    const { data: available_fonts } = await this.$api.get(
      `/api/v3/one-page-sites/fonts`
    )

    await commit('setProperty', {
      key: 'available_fonts',
      value: available_fonts,
    })
  },

  /**
   * Get social icon types
   */
  async getSocialIconTypes({ commit }) {
    const { data: social_icon_types } = await this.$api.get(
      `/api/v3/one-page-sites/social-icon-types`
    )

    await commit('setProperty', {
      key: 'social_icon_types',
      value: social_icon_types,
    })
  },

  /**
   * Get social link from handle
   */
  async getSocialLink(context, { handle, icon }) {
    const query = {
      handle,
      icon,
    }
    const queryParams = new URLSearchParams(query).toString()
    const { data } = await this.$api.get(
      `/api/v3/one-page-sites/social-link?${queryParams}`
    )

    return data.link
  },

  /**
   * Get meta from href/url
   */
  async getMeta(context, { href }) {
    const query = {
      href,
    }
    const queryParams = new URLSearchParams(query).toString()
    const { data } = await this.$api.get(
      `/api/v3/one-page-sites/meta?${queryParams}`
    )

    return data
  },

  /**
   * Get Google Maps Autocomplete predictions
   */
  async getGoogleMapsAutocomplete(context, { input }) {
    const query = {
      input,
    }
    const queryParams = new URLSearchParams(query).toString()
    const { data } = await this.$api.get(
      `/api/v3/one-page-sites/maps/autocomplete?${queryParams}`
    )

    return data
  },

  async createUpload(context, { file, name }) {
    const payload = new FormData()

    payload.append('file', file)
    payload.append('name', name)
    const { data } = await this.$api.post(`/api/v3/stitch/uploads`, payload)

    return data
  },

  async retrieveUpload(context, { id }) {
    const { data } = await this.$api.get(`/api/v3/stitch/uploads/${id}`)

    return data
  },

  async listUploads(context, {}) {
    const { data } = await this.$api.get(`/api/v3/stitch/uploads`)

    return data
  },
}
